import ReactEChartsCore from 'echarts-for-react/lib/core';
import { echarts, BRAND_COLOR } from '../../shared/echarts-config';
import { ChartContainer } from '../../shared/ChartContainer';
import { EmptyState } from '../../shared/EmptyState';
import type { FpSearchVolumeData } from '../../../../types/analytics.types';

type SearchVolumeHeatmapProps = Pick<
  FpSearchVolumeData,
  'categories' | 'states' | 'matrix' | 'max'
>;

const TITLE = 'Search Volume by Category & Geography';
const SUBTITLE = 'Where each service is searched most (top categories × states)';

const buildOptions = ({ categories, states, matrix, max }: SearchVolumeHeatmapProps) => {
  // ECharts heatmap wants [xIndex, yIndex, value]; matrix is row-major [category][state].
  const data: number[][] = [];
  matrix.forEach((row, catIdx) => {
    row.forEach((value, stateIdx) => data.push([stateIdx, catIdx, value]));
  });

  return {
    tooltip: {
      position: 'top' as const,
      formatter: (p: { data: number[] }) =>
        `${categories[p.data[1]]} · ${states[p.data[0]]}: ${p.data[2]}`,
    },
    grid: { left: 8, right: 8, top: 8, bottom: 64, containLabel: true },
    xAxis: {
      type: 'category' as const,
      data: states,
      splitArea: { show: true },
      axisLabel: { fontSize: 11, color: '#6b7280' },
    },
    yAxis: {
      type: 'category' as const,
      data: categories,
      splitArea: { show: true },
      axisLabel: { fontSize: 11, color: '#6b7280' },
    },
    visualMap: {
      min: 0,
      max: Math.max(max, 1),
      calculable: true,
      orient: 'horizontal' as const,
      left: 'center',
      bottom: 10,
      inRange: { color: ['#eef4f3', BRAND_COLOR] },
    },
    series: [
      {
        name: 'Searches',
        type: 'heatmap',
        data,
        label: { show: false },
        emphasis: { itemStyle: { shadowBlur: 6, shadowColor: 'rgba(0, 0, 0, 0.2)' } },
      },
    ],
  };
};

export const SearchVolumeHeatmap = ({
  categories,
  states,
  matrix,
  max,
}: SearchVolumeHeatmapProps) => {
  if (categories.length === 0 || states.length === 0 || max === 0) {
    return (
      <ChartContainer title={TITLE} subtitle={SUBTITLE}>
        <EmptyState message="No search volume for the selected filters." />
      </ChartContainer>
    );
  }

  return (
    <ChartContainer title={TITLE} subtitle={SUBTITLE}>
      <ReactEChartsCore
        echarts={echarts}
        option={buildOptions({ categories, states, matrix, max })}
        style={{ height: Math.max(240, categories.length * 36 + 120) }}
        notMerge
        lazyUpdate
      />
    </ChartContainer>
  );
};
